import {
	ApiStakeSuiFrenBody,
	SuiNetwork,
	SuiFrenObject,
	Url,
	Nft,
	Balance,
	SuiFrenAccessoryType,
	ApiAddSuiFrenAccessoryBody,
	ApiRemoveSuiFrenAccessoryBody,
	SuiFrenAccessoryObject,
	ApiAccessoriesForSuiFrenBody,
	AnyObjectType,
	ObjectId,
	SuiAddress,
	CallerConfig,
} from "../../types";
import { Caller } from "../../general/utils/caller";
import { format } from "date-fns";
import { Coin } from "..";
import { AftermathApi } from "../../general/providers";

export class SuiFren extends Caller {
	// =========================================================================
	//  Constructor
	// =========================================================================

	constructor(
		public readonly suiFren: SuiFrenObject,
		config?: CallerConfig,
		public readonly isStaked: boolean = false,
		public readonly isOwned: boolean = false,
		public readonly api?: AftermathApi
	) {
		super(config, "sui-frens");
	}

	// =========================================================================
	//  Getters
	// =========================================================================

	public suiFrenType(): AnyObjectType {
		return Coin.getInnerCoinType(this.suiFren.objectType);
	}

	public properties(): Record<string, string> {
		return {
			Skin: this.suiFren.attributes.skin,
			Ears: this.suiFren.attributes.ears,
			Expression: this.suiFren.attributes.expression,
			"Main Color": this.suiFren.attributes.main,
			"Secondary Color": this.suiFren.attributes.secondary,
			"Birth Location": this.suiFren.birthLocation,
			Birthday: format(this.suiFren.birthdate, "MMMM d, yyyy"),
			Cohort: this.suiFren.cohort.toString(),
			Generation: this.suiFren.generation.toString(),
			// Genes: this.suiFren.genes.toString(),
		};
	}

	public dynamicFields(): Record<string, string> {
		return {
			...(this.suiFren.mixLimit
				? {
						"Mixes Remaining": this.suiFren.mixLimit.toString(),
					}
				: {}),
			...(this.suiFren.lastEpochMixed
				? {
						"Last Epoch Mixed": this.suiFren.lastEpochMixed.toString(),
					}
				: {}),
		};
	}

	public displayNumber(): string {
		return this.suiFren.objectId.slice(-5, -1).toUpperCase();
	}

	public clone(): SuiFren {
		return new SuiFren(this.suiFren, this.config, this.isStaked, this.isOwned);
	}

	// public asNft(): Nft {
	// 	return {
	// 		info: {
	// 			objectId: this.suiFren.objectId,
	// 			objectType: this.suiFren.objectType,
	// 		},
	// 		display: {
	// 			suggested: {
	// 				name: "SuiFren",
	// 				...this.suiFren.display,
	// 			},
	// 			other: {
	// 				Skin: this.suiFren.attributes.skin,
	// 				Ears: this.suiFren.attributes.ears,
	// 				Expression: this.suiFren.attributes.expression,
	// 				"Main Color": this.suiFren.attributes.main,
	// 				"Secondary Color": this.suiFren.attributes.secondary,
	// 				"Birth Location": this.suiFren.birthLocation,
	// 				Birthday: dayjs(this.suiFren.birthdate).format(
	// 					"MMMM D, YYYY"
	// 				),
	// 				Cohort: this.suiFren.cohort.toString(),
	// 				Generation: this.suiFren.generation.toString(),
	// 				...(this.suiFren.mixLimit
	// 					? {
	// 							"Mixes Remaining":
	// 								this.suiFren.mixLimit.toString(),
	// 					  }
	// 					: {}),
	// 				...(this.suiFren.lastEpochMixed
	// 					? {
	// 							"Last Epoch Mixed":
	// 								this.suiFren.lastEpochMixed.toString(),
	// 					  }
	// 					: {}),
	// 				// Genes: this.suiFren.genes.toString(),
	// 			},
	// 		},
	// 	};
	// }

	// =========================================================================
	//  Objects
	// =========================================================================

	public async getAccessories() {
		return this.fetchApi<
			SuiFrenAccessoryObject[],
			ApiAccessoriesForSuiFrenBody
		>("accessories", {
			suiFrenId: this.suiFren.objectId,
		});
	}

	// =========================================================================
	//  Transactions
	// =========================================================================

	public async getStakeTransaction(inputs: {
		baseFee: Balance;
		feeIncrementPerMix: Balance;
		minRemainingMixesToKeep: bigint;
		walletAddress: SuiAddress;
	}) {
		if (this.isStaked)
			throw new Error("unable to stake already staked suiFren");

		return this.suiFrensApi().fetchStakeTx({
			...inputs,
			suiFrenType: this.suiFrenType(),
			suiFrenId: this.suiFren.objectId,
		});
	}

	public async getAddAccessoryTransaction(inputs: {
		accessoryId: ObjectId;
		walletAddress: SuiAddress;
	}) {
		return this.suiFrensApi().fetchBuildAddAccessoryTx({
			...inputs,
			isOwned: this.isOwned,
			suiFrenType: this.suiFrenType(),
			suiFrenId: this.suiFren.objectId,
		});
	}

	public async getRemoveAccessoryTransaction(inputs: {
		accessoryType: SuiFrenAccessoryType;
		walletAddress: SuiAddress;
	}) {
		if (!this.isOwned)
			throw new Error(
				"unable to remove accessory from suiFren that is not owned by caller"
			);

		return this.suiFrensApi().fetchBuildRemoveAccessoryTx({
			...inputs,
			suiFrenType: this.suiFrenType(),
			suiFrenId: this.suiFren.objectId,
		});
	}

	// =========================================================================
	//  Private Helpers
	// =========================================================================

	private suiFrensApi = () => {
		const suiFrens = this.api?.SuiFrens();
		if (!suiFrens) {
			throw new Error("missing AftermathApi instance");
		}
		return suiFrens;
	};
}
